import { useState } from "react";
import { createListStore } from "../../common/listStore";
import { PageTitle } from "../../layout/PageTitle";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { Edit } from "@mui/icons-material";
import { Button, Grid } from "@mui/material";
import { RunDetailDialog } from "./RunDetailDialog";
import { useDialog } from "../../common/dialog";
import { RunType, useRunTypes } from "./common";
import { createEntityStore } from "../../common/entityStore";
import { propValidValue } from "../../common/entityStoreValidators";
import { propDateField, propEnumField, propTextField } from "../../common/entityStoreForm";
import { nextDay, applyListStoreFilters } from "../../common/listStoreFilters";

function getFiltersStoreInitialData() {
    return {};
}

export function RunHistoryPage() {
    const [{ useStore, useFiltersStore }] = useState(() => {
        const useStore = createListStore<RunDto>({
            path: "runs/runs/list",
            autoInitialFetch: true,
            supportsApiFilterSortPage: true,
            itemsPerPage: 15,
            listProps: [
                { column: "id", label: "ID", width: 70 },
                { column: "runType", label: "Type", width: 200, enumStore: useRunTypes },
                { column: "createdAt", label: "Started at", type: "datetime", width: 150 },
                { column: "finishedAt", label: "Finished at", type: "datetime", width: 150 },
                { column: "userName", label: "User", width: 150 },
                { column: "isFake", label: "Is fake", type: "bool", width: 100 },
                { column: "isRunning", label: "Is running", type: "bool", width: 100 },
            ],
        });
        const useFiltersStore = createEntityStore<StoreFilters>({
            data: getFiltersStoreInitialData(), isValidationEnabled: true,
            propValidators: [
                propValidValue("from", "fromFormatted"),
                propValidValue("to", "toFormatted"),
            ]
        });
        applyListStoreFilters(useFiltersStore, useStore, [
            { listProps: ["createdAt"], operator: "GreaterThanOrEqual", filterProp: "from" },
            { listProps: ["createdAt"], operator: "LessThan", filterProp: "to", transform: nextDay },
            { listProps: ["userName"], operator: "Like", filterProp: "user" },
            { listProps: ["runType"], operator: "Equal", filterProp: "runType" },
        ]);
        return { useStore, useFiltersStore };
    });

    const selectedItem = useStore(p => p.selectedItem);
    const detailDialog = useDialog(close => <RunDetailDialog close={close} id={selectedItem!.id} runType={selectedItem!.runType} />, "lg");

    function clearFiltersStore() {
        useFiltersStore.getState().setClientData(getFiltersStoreInitialData());
    }

    return <>
        <PageTitle title="Run history" />
        {detailDialog.render()}
        {renderListStore(useStore, {
            header: listHeaderItems([
                ["detail", () => <Button title="Detail" disabled={selectedItem == null} onClick={() => detailDialog.show()} color="edit"><Edit /></Button>]
            ]),
            onDoubleClick: () => detailDialog.show(),
            filtersMaxWidth: "xl",
            renderExternalFilters: () => <>
                <Grid item sm={4} md={2}>{propDateField(useFiltersStore)("from", "fromFormatted", "Date from")}</Grid>
                <Grid item sm={4} md={2}>{propDateField(useFiltersStore)("to", "toFormatted", "Date to")}</Grid>
                <Grid item sm={4} md={2}>{propEnumField(useFiltersStore)("runType", "Run type", useRunTypes, { addAll: true })}</Grid>
                <Grid item sm={4} md={2}>{propTextField(useFiltersStore)("user", "User")}</Grid>
            </>, clearExternalFilters: clearFiltersStore,
        })}
    </>
}

interface RunDto {
    id: number;
    runType: RunType;
    createdAt: string;
    finishedAt: string | null;
    userName: string;
    isFake: boolean;
    isRunning: boolean;
}


interface StoreFilters {
    runType?: RunType;
    user?: string;
    from?: string;
    to?: string;
    fromFormatted?: string;
    toFormatted?: string;
}
